import { Router } from '@angular/router';
import { ITokenModel } from '@delon/auth';

import { BaseLogic } from './base.logic';
import { MenuLogic } from './menu.logic';

import { CookieService } from '@delon/util';
import { IApplicationData, IKVPaire } from '@shared/model';
import { REDIRECT_URI } from 'src/app/shared/consts/cache.consts';
import { isWorkWeixinBrowser } from '@shared';

export class ApplicationLogic extends BaseLogic {
  router: any;
  /**
   * 加载应用信息
   *
   * @author 王阮强(wangruanqiang@youzhibo.cn)
   * @date 2021-02-04
   * @param {ITokenModel} tokenData token数据
   * @param {IApplicationData} [appData=null] 应用数据
   */
  load(tokenData: ITokenModel, appData: IApplicationData | null = null): void {
    // 设置token
    this.tokenSrv.set(tokenData);
    // 设置cookie的token
    this.injector.get<CookieService>(CookieService).put('token', tokenData.token as string, {
      expires: tokenData.expired
    });
    if (null != appData) {
      let { user, functions, menu, appType }: IApplicationData = appData;
      // 保存用户信息
      if (user) this.privilegeSrv.user = user;
      // 保存菜单信息
      if (menu) this.privilegeSrv.menu = menu;
      // 保存功能编码信息
      if (functions) this.privilegeSrv.functions = functions;
      // 保存应用类型
      if (appType) this.privilegeSrv.appType = appType;
    } else {
      this.privilegeSrv.refresh();
    }
    // 加载用户信息
    this.settingsSrv.setUser({
      name: this.privilegeSrv.user?.realname,
      avatar: this.privilegeSrv.user?.img_head,
      ...(this.privilegeSrv.user || {})
    });
    // if (1 == this.privilegeSrv.appType) {
    // }
    // this.menuSrv.add(this.privilegeSrv.menu);
    // 配置权限信息
    this.aclSrv.setAbility(this.privilegeSrv.funcode);
    setTimeout(() => {
      // 加载菜单信息
      this.menuSrv.clear();
      this.menuSrv.add(MenuLogic.getInstance(this.injector).load(this.privilegeSrv.menu!));
    }, 10);
  }

  /**
   * 跳转至登录页面
   *
   * @author 王阮强(wangruanqiang@youzhibo.cn)
   * @date 2021-02-02
   * @private
   * @param {string} [content=null] 提示框详细文本
   * @param {string} [title=null] 提示框标题
   * @param {number} [appType=null] 跳转应用类型
   */
  toLogin(
    content: string | null = null,
    title: string | null = null,
    appType: number | null = null,
    queryParams: IKVPaire | null = null
  ): void {
    this.privilegeSrv.clear();
    // 页面提示
    if (null !== content && null !== title) {
      this.notificationSrv.error(title, content);
    }
    // 根据当前应用类型决定跳转登录页面地址
    appType = appType || 3;
    this.privilegeSrv.appType = appType;
    const appTypeName = this.privilegeSrv.getAppTypeName(appType);
    // 判断企微打开页面时，登录页固定添加workweixin节点
    // 即 /{appName}/workweixin/passport/login 固定格式
    let workweixin = '';
    if (isWorkWeixinBrowser()) {
      workweixin = '/workweixin';
    }
    // 登录页面判断
    const loginUrl = this.privilegeSrv.loginUrl ? this.privilegeSrv.loginUrl : `/${appTypeName}${workweixin}${this.tokenSrv.login_url}`;
    this.tokenSrv.clear();
    this.injector.get(CookieService).removeAll();
    // 判断当前页面是否登录页，如果是则无需重复跳转
    const url = new URL(location.href),
      path = url.hash ? url.hash.substring(1) : url.pathname;
    // 登录页或者根目录 / 无需跳转
    if (path.indexOf('/passport/login') >= 0 || '/' == path || path.indexOf('/wxwork/wxwork') >= 0) {
      return;
    }
    // 缓存跳转地址
    this.cacheSrv.set(REDIRECT_URI, decodeURIComponent(path), {
      type: 's',
      expire: 600
    });
    this.goTo(loginUrl, {
      redirect_uri: encodeURIComponent(path),
      ...(queryParams || {})
    });
  }
  /**
   * 跳转首页
   */
  toHome() {
    // 获取当前模块名
    const module = this.privilegeSrv.appTypeName,
      // 获取当前本地是否有缓存重定向地址
      redirectUri = this.cacheSrv.get(REDIRECT_URI, {
        mode: 'none'
      });
    this.cacheSrv.remove(REDIRECT_URI);
    this.goTo(redirectUri || module);
  }
  /**
   * 跳转页面
   */
  /**
   * 跳转指定页面
   *
   * @param {string} path 跳转地址，相对路径
   * @param {(IKVPaire | null)} [queryParams=null] 跳转queryString参数
   */
  goTo(path: string, queryParams: IKVPaire | null = null): void {
    setTimeout(() => {
      this.injector.get(Router).navigate(
        [path],
        queryParams
          ? {
              queryParams
            }
          : undefined
      );
    });
  }
  /**
   * 应用程序初始化，处理token等信息
   */
  init(appType: number, loginUrl: string | null = null): void {
    // 配置登录地址
    this.privilegeSrv.loginUrl = loginUrl;
    // 验证访问的应用和权限中的应用不一致，则跳转登录
    if (appType != this.privilegeSrv.appType) {
      return this.toLogin(null, null, appType);
    }
    // 判断本地token是否有效
    const tokenData = (this.tokenSrv.get() || {}) as ITokenModel;
    if (Object.keys(tokenData).length > 0) {
      if (!tokenData.expired || tokenData.expired < +new Date()) {
        return this.toLogin('登录令牌已过期', '登陆失效');
      }
    } else {
      return this.toLogin(null, null, appType);
    }
    // token有效，则使用token数据重新初始化
    return this.load(tokenData);
  }
}
